import { Controller } from "@hotwired/stimulus"

// The contact's edit form, in a modal dialog. Scoped to the whole card, not just
// the details block: the displayed location / role / birthday lines are
// click-to-edit hotspots that open the dialog focused on their own field
// (data-contact-edit-field-param -> data-contact-edit-field).
//
// WHY A DIALOG. This used to swap the read-only rows out for the form in place
// and take over the pinned footer for Save/Cancel. In the 320px sidebar that
// meant giving up the card you were reading in order to change one field, and
// every pencil on the card led to the same disappearing act. showModal() keeps
// the card visible behind the form and behaves identically on both surfaces.
//
// The browser owns Escape, the focus trap and the backdrop, so the only things
// left here are which field to focus, discarding edits on close, and treating a
// click on the backdrop as a dismissal.
export default class extends Controller {
    static targets = ["dialog"]

    // The inline "Add email" / "Add phone" disclosures on the card. <details>
    // gives no way to focus its own content on open, and without this you click
    // the summary and then have to click the field it just revealed. Guarded on
    // .open because toggle fires on collapse too, where there is nothing to
    // focus. Opening one closes the other: they share a line.
    addToggled(event) {
        const opened = event.target
        if (!opened.open) {
            // An add row whose slot is already filled (a work address, a
            // number) is tucked away until the Contact menu asks for it, and
            // goes back once it is closed.
            if (opened.dataset.quiet === "true") opened.hidden = true
            return
        }

        this.element.querySelectorAll("details.contact-reach-add[open]").forEach((other) => {
            if (other !== opened) other.open = false
        })
        opened.querySelector("input:not([type=hidden])")?.focus()
    }

    // About's ⋯ "Add a note": opens About and its note form, under the first
    // line, cursor in the field. Cancel puts it away (2026-10-14).
    addNote(event) {
        event.preventDefault()
        const about = this.element.querySelector("details.contact-about")
        const box = about?.querySelector(".contact-about-add")
        if (!box) return

        about.open = true
        box.hidden = false
        const form = box.querySelector("details.memory-add")
        if (form) form.open = true
        box.querySelector("textarea")?.focus()
    }

    // A section heading's ⋯ that stands for a control inside the section
    // (People & Relations' "Add a relationship"): open the section, press it.
    press(event) {
        event.preventDefault()
        const target = this.element.querySelector(event.params.selector)
        if (!target) return

        const fold = target.closest("details.contact-v3-fold")
        if (fold && !fold.open) fold.open = true
        target.click()
        target.scrollIntoView({ block: "nearest" })
    }

    closeNote(event) {
        event.preventDefault()
        const box = event.currentTarget.closest(".contact-about-add")
        if (box) box.hidden = true
    }

    // The Contact heading's ⋯ "Add email" / "Add phone": opens that add row,
    // unfolding the section and showing the rows first if they were tucked
    // away.
    addReach(event) {
        event.preventDefault()
        const add = this.element.querySelector(`details.contact-reach-add[data-kind="${event.params.kind}"]`)
        if (!add) return

        const fold = add.closest("details.contact-v3-fold")
        if (fold && !fold.open) fold.open = true
        add.hidden = false
        if (add.open) add.querySelector("input:not([type=hidden])")?.focus()
        else add.open = true
    }

    toggle(event) {
        event.preventDefault()
        this.dialogTarget.open ? this.close(event) : this.open(event)
    }

    open(event) {
        event?.preventDefault()
        if (this.dialogTarget.open) return

        this.element.querySelector(".contact-edit-dialog-form")?.reset()
        this.dialogTarget.showModal()
        this._focus(event?.params?.field)
    }

    close(event) {
        event?.preventDefault()
        this.element.querySelector(".contact-edit-dialog-form")?.reset()
        this.dialogTarget.close()
    }

    // The native close event is queued. If someone has already reopened the
    // dialog, that old event must not erase edits from the new session.
    closed() {
        if (!this.dialogTarget.open) this.element.querySelector(".contact-edit-dialog-form")?.reset()
    }

    // A click that lands on the dialog element itself is a click on the backdrop:
    // the form fills the dialog, so anything inside hits a descendant.
    backdrop(event) {
        if (event.target === this.dialogTarget) this.close(event)
    }

    // :not([type=hidden]) matters: the form opens with several hidden fields
    // (details, surface, row context), and focusing one silently does nothing —
    // leaving the browser's own default, the close button, focused instead.
    _focus(field) {
        const target = field ? this.dialogTarget.querySelector(`[data-contact-edit-field="${field}"]`) : null
        const element = target || this.dialogTarget.querySelector("input:not([type=hidden]), select, textarea")
        element?.focus()
    }
}
